import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/joy';
import {
  Envelope as EmailIcon,
  Trash as DeleteIcon,
  ArrowsClockwise as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  GoogleLogo as GoogleIcon,
  WindowsLogo as MicrosoftIcon
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { ProfileForm } from '../../components/settings/ProfileForm';
import { PhoneAndCallingCard } from '../../components/settings/PhoneAndCallingCard';
import { PasswordChangeForm } from '../../components/settings/PasswordChangeForm';
import { PageHeader } from '../../components/common/PageHeader';

interface EmailAccount {
  id: number;
  email_address: string;
  display_name: string;
  provider: 'google' | 'microsoft';
  is_active: boolean;
  last_sync_at: string | null;
  last_sync_error?: string;
  backfill_status?: 'pending' | 'running' | 'done';
  created_at: string;
}

export const ProfilePage: React.FC = () => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<EmailAccount | null>(null);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'danger' | 'warning'; message: string } | null>(null);
  const queryClient = useQueryClient();

  // Check for OAuth callback parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');
    const email = params.get('email');

    if (success === 'email_connected' && email) {
      setAlertMessage({
        type: 'success',
        message: `Successfully connected email account: ${email}`
      });
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refetch accounts
      queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });
    } else if (error) {
      const details = params.get('details');
      setAlertMessage({
        type: 'danger',
        message: `Failed to connect email account: ${error}${details ? ` - ${details}` : ''}`
      });
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [queryClient]);

  // Fetch connected email accounts
  const { data: accounts, isLoading, isError, refetch } = useQuery<EmailAccount[]>({
    queryKey: ['emailAccounts'],
    queryFn: async () => {
      const response = await api.get('/api/inbox/oauth/accounts/');
      return (response.data as { accounts: EmailAccount[] }).accounts;
    }
  });

  // Disconnect account mutation
  const disconnectMutation = useMutation({
    mutationFn: async (accountId: number) => {
      await api.post(`/api/inbox/oauth/accounts/${accountId}/disconnect/`, {});
    },
    onSuccess: () => {
      setAlertMessage({
        type: 'success',
        message: 'Email account disconnected successfully'
      });
      queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });
      setDeleteModalOpen(false);
      setAccountToDelete(null);
    },
    onError: () => {
      setAlertMessage({
        type: 'danger',
        message: 'Failed to disconnect email account'
      });
    }
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: async (accountId: number) => {
      const response = await api.post(`/api/inbox/oauth/accounts/${accountId}/test/`, {});
      return response.data as { success: boolean; message: string };
    },
    onSuccess: (data) => {
      setAlertMessage({
        type: data.success ? 'success' : 'warning',
        message: data.message
      });
    },
    onError: () => {
      setAlertMessage({
        type: 'danger',
        message: 'Failed to test connection'
      });
    }
  });

  const handleConnectEmail = async (provider: 'google' | 'microsoft') => {
    try {
      // Fetch OAuth URL from backend
      const response = await api.get<{ auth_url: string }>(`/api/inbox/oauth/connect/${provider}/`);
      // Redirect to OAuth provider
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Failed to initiate OAuth flow:', error);
      const label = provider === 'google' ? 'Gmail' : 'Outlook';
      const serverMessage = (error as any)?.responseData?.error;
      setAlertMessage({
        type: 'danger',
        message: serverMessage || `Couldn't start ${label} sign-in. Please try again.`,
      });
    }
  };

  const handleDeleteAccount = (account: EmailAccount) => {
    setAccountToDelete(account);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (accountToDelete) {
      disconnectMutation.mutate(accountToDelete.id);
    }
  };

  const getProviderIcon = (provider: string) => {
    if (provider === 'google') {
      return <GoogleIcon />;
    } else if (provider === 'microsoft') {
      return <MicrosoftIcon />;
    }
    return <EmailIcon />;
  };

  const getProviderName = (provider: string) => {
    if (provider === 'google') {
      return 'Gmail';
    } else if (provider === 'microsoft') {
      return 'Outlook';
    }
    return provider;
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <PageHeader title="Profile & email" description="Connect your mailbox to send email from ApexOS, then manage your profile and password." />

      {alertMessage && (
        <Alert
          color={alertMessage.type}
          sx={{ mb: 3 }}
          endDecorator={
            <IconButton
              variant="plain"
              size="sm"
              color={alertMessage.type}
              onClick={() => setAlertMessage(null)}
            >
              ×
            </IconButton>
          }
        >
          {alertMessage.message}
        </Alert>
      )}

      {/* Email Accounts Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 2 }}>
            <Typography level="title-lg">
              Connected Email Accounts
            </Typography>
            <Button
              size="sm"
              variant="soft"
              startDecorator={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </Box>

          <Typography level="body-sm" sx={{ mb: 3 }}>
            Connect your email accounts to manage all communications in one place.
          </Typography>

          {isError ? <Alert color="danger" endDecorator={<Button size="sm" variant="soft" color="danger" onClick={() => refetch()}>Retry</Button>}>Could not load connected email accounts.</Alert> : isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {accounts && accounts.length > 0 ? (
                <List sx={{ mb: 3 }}>
                  {accounts.map((account) => (
                    <ListItem key={account.id}>
                      <ListItemDecorator>
                        {getProviderIcon(account.provider)}
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="title-sm">
                          {account.display_name || account.email_address}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography level="body-sm">
                            {account.email_address}
                          </Typography>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={account.is_active ? 'success' : 'warning'}
                            startDecorator={account.is_active ? <CheckCircleIcon /> : <WarningIcon />}
                          >
                            {account.is_active ? 'Active' : 'Inactive'}
                          </Chip>
                          <Chip size="sm" variant="outlined">
                            {getProviderName(account.provider)}
                          </Chip>
                          {account.is_active && account.backfill_status && account.backfill_status !== 'done' && (
                            <Chip size="sm" variant="soft" color="primary">
                              Importing email history…
                            </Chip>
                          )}
                        </Box>
                        {account.last_sync_at && (
                          <Typography level="body-xs" sx={{ mt: 0.5 }}>
                            Last synced: {new Date(account.last_sync_at).toLocaleString()}
                          </Typography>
                        )}
                      </ListItemContent>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={() => testConnectionMutation.mutate(account.id)}
                          disabled={testConnectionMutation.isPending}
                        >
                          <RefreshIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleDeleteAccount(account)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert variant="soft" color="neutral" sx={{ mb: 3 }}>
                  No email accounts connected yet. Connect your Gmail or Outlook account to get started.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography level="title-sm" sx={{ mb: 2 }}>
                Add Email Account
              </Typography>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="outlined"
                  color="primary"
                  startDecorator={<GoogleIcon />}
                  onClick={() => handleConnectEmail('google')}
                >
                  Connect Gmail
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startDecorator={<MicrosoftIcon />}
                  onClick={() => handleConnectEmail('microsoft')}
                >
                  Connect Outlook
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      {/* Phone & Calling Section */}
      <PhoneAndCallingCard />

      {/* User Profile Section */}
      <ProfileForm />
      
      {/* Password & Security Section */}
      <Box sx={{ mt: 3 }}>
        <PasswordChangeForm />
      </Box>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningIcon />
            Disconnect Email Account
          </DialogTitle>
          <Divider />
          <DialogContent>
            Are you sure you want to disconnect {accountToDelete?.email_address}?
            This will stop syncing emails from this account.
          </DialogContent>
          <DialogActions>
            <Button variant="solid" color="danger" onClick={confirmDelete}>
              Disconnect
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
